/**
 * Lightweight safety filter — keeps all voiced output warm + non-toxic, per the
 * triple contract (CLAUDE.md): the world must never make the player feel bad,
 * judged, or behind. This is a cheap, deterministic last line of defense applied
 * to BOTH backends' output (the LLM is also system-prompted, but we never trust a
 * model blindly). It scrubs at the word level and softens hostile emotions.
 */
import type { Emotion } from '@/shared';

/**
 * Unkind / harsh tokens we never want aimed outward in a cozy town. Kept small +
 * obvious; this is a floor, not a comprehensive moderation system. Matched on
 * word boundaries, case-insensitively.
 */
const UNKIND_WORDS: readonly string[] = [
  'stupid',
  'idiot',
  'idiots',
  'dumb',
  'loser',
  'losers',
  'hate',
  'hates',
  'hated',
  'worthless',
  'pathetic',
  'useless',
  'trash',
  'garbage',
  'noob',
  'noobs',
  'ugly',
  'shut up',
  'shutup',
  'suck',
  'sucks',
  'sucked',
  'weak',
  'failure',
  'terrible',
  'awful',
  'hopeless',
  'lame',
];

/** Warm replacement tokens — picked deterministically by word length so the same
 * input always scrubs the same way (keeps the whole voice layer reproducible). */
const WARM_REPLACEMENTS: readonly string[] = ['lovely', 'kind', 'sweet', 'wonderful', 'gentle'];

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// One alternation, word-boundary anchored, case-insensitive. Multi-word phrases
// (e.g. "shut up") are handled by allowing internal whitespace in the pattern.
const UNKIND_PATTERN = new RegExp(
  '\\b(' + UNKIND_WORDS.map((w) => escapeRegExp(w).replace(/\\?\s+/g, '\\s+')).join('|') + ')\\b',
  'gi',
);

function pickReplacement(match: string): string {
  const idx = match.length % WARM_REPLACEMENTS.length;
  return WARM_REPLACEMENTS[idx]!;
}

/**
 * Scrub a single line: replace unkind words with warm ones, preserving readable
 * spacing. Never throws; empty in ⇒ empty out.
 */
export function scrubLine(text: string): string {
  if (!text) return text;
  const replaced = text.replace(UNKIND_PATTERN, (m) => pickReplacement(m));
  // Collapse any accidental double spaces introduced by edits; trim edges lightly.
  return replaced.replace(/[ \t]{2,}/g, ' ');
}

/** True if the text contains no flagged unkindness (post-trim, case-insensitive). */
export function isWarm(text: string): boolean {
  UNKIND_PATTERN.lastIndex = 0;
  const result = !UNKIND_PATTERN.test(text);
  UNKIND_PATTERN.lastIndex = 0;
  return result;
}

/**
 * Map any incoming emotion to a non-hostile one. `angry` could read as hostility
 * aimed at the player, so it is softened; everything else (including the wistful
 * `sad`/`sigh`/`worried`) is gentle enough to keep.
 */
export function softenEmotion(emotion: string): Emotion {
  const valid: readonly Emotion[] = [
    'normal',
    'happy',
    'sad',
    'angry',
    'surprised',
    'worried',
    'joyous',
    'determined',
    'sigh',
    'inspired',
  ];
  if (emotion === 'angry') return 'determined';
  return (valid as readonly string[]).includes(emotion) ? (emotion as Emotion) : 'normal';
}
